import { EmpresasCombinacionModal } from '@/components/EmpresasCombinacionModal';
import { buildGruposDesdeTernas, MatrizAsociaciones, type GrupoTernas } from '@/components/MatrizAsociaciones';
import { EditarRequisitoModal } from '@/components/sysadmin/EditarRequisitoModal';
import { Badge, LoadingContainer, Modal, Select } from '@/components/ui';
import { useCombinaciones, useTernas } from '@/hooks/useSysadmin';
import type { RequisitoAsociacion, RequisitoSysadmin } from '@/services/sysadmin.service';
import { sysadminService } from '@/services/sysadmin.service';
import { AlertTriangle, Building2, Check, ChevronDown, ChevronsUpDown, Eye, Info, Link2, Pencil, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

export function GestionAsociacionesPage() {
  const { combinaciones, isLoading: loadingCombinaciones } = useCombinaciones();
  const [selectedCombinacionId, setSelectedCombinacionId] = useState<number | undefined>(undefined);
  const [editMode, setEditMode] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showEmpresasModal, setShowEmpresasModal] = useState(false);
  const [migrando, setMigrando] = useState(false);
  const [editingRequisito, setEditingRequisito] = useState<RequisitoSysadmin | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [viabilidadResult, setViabilidadResult] = useState<import('@/types/types').ViabilidadRequisitoResponse | null>(null);
  const [showViabilidadModal, setShowViabilidadModal] = useState(false);
  const [checkingViabilidad, setCheckingViabilidad] = useState(false);

  // Estados para Análisis y Migración
  const [analysisData, setAnalysisData] = useState<import('@/types/types').AnalisisDetalladoRequisito | null>(null);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [showMigrationWizard, setShowMigrationWizard] = useState(false);
  const [sourceRequisitoId, setSourceRequisitoId] = useState<string | null>(null);
  const [isSelectingTarget, setIsSelectingTarget] = useState(false);
  const [migrationStep, setMigrationStep] = useState(1);
  const [targetRequisitoId, setTargetRequisitoId] = useState<string | null>(null);
  const [migrating, setMigrating] = useState(false);
  const [migrationResult, setMigrationResult] = useState<import('@/types/types').MigrarRequisitoResponse['data'] | null>(null);

  // Estados para Análisis en vivo dentro del Wizard
  const [sourceAnalysis, setSourceAnalysis] = useState<import('@/types/types').AnalisisDetalladoRequisito | null>(null);
  const [targetAnalysis, setTargetAnalysis] = useState<import('@/types/types').AnalisisDetalladoRequisito | null>(null);
  const [loadingWizardAnalysis, setLoadingWizardAnalysis] = useState(false);

  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useTernas(selectedCombinacionId);

  // Mapa global de requisitos por ID para búsquedas rápidas en el wizard
  const requisitosMap = useMemo(() => {
    const map = new Map<string, RequisitoAsociacion>();
    if (!data?.requisitos) return map;
    data.requisitos.forEach((r) => {
      map.set(r.requisitoId, r);
    });
    return map;
  }, [data?.requisitos]);

  // Grupos de asociaciones (calculados a partir de ternas)
  const grupos = useMemo<GrupoTernas[]>(() => {
    if (!data) return [];
    return buildGruposDesdeTernas(data.ternas, data.requisitosSinTerna, data.requisitos, data.combinacion.ntcs);
  }, [data]);

  // Stats
  const stats = useMemo(() => {
    if (!data) return { totalRequisitos: 0, totalTernas: 0, clusters: 0, reqsSinAsociar: 0 };
    const totalRequisitos = data.requisitos.length;
    const totalTernas = data.ternas.length;
    const reqsSinAsociar = data.requisitosSinTerna.length;
    return { totalRequisitos, totalTernas, clusters: grupos.length, reqsSinAsociar };
  }, [data, grupos]);

  // Asignar requisito a una celda de terna
  const handleAsignarRequisito = useCallback(
    async (ternaId: number, columnaOrden: number, requisitoId: string) => {
      setActionError(null);
      try {
        const campo = `requisitoC${columnaOrden}Id` as 'requisitoC1Id' | 'requisitoC2Id' | 'requisitoC3Id';
        await sysadminService.patchTerna(ternaId, { [campo]: requisitoId });
        await refetch();
      } catch (err) {
        setActionError(err instanceof Error ? err.message : 'Error al asignar requisito');
      }
    },
    [refetch]
  );

  // Desvincular requisito de una celda de terna
  const handleDesvincularRequisito = useCallback(
    async (ternaId: number, columnaOrden: number) => {
      setActionError(null);
      try {
        const campo = `requisitoC${columnaOrden}Id` as 'requisitoC1Id' | 'requisitoC2Id' | 'requisitoC3Id';
        await sysadminService.patchTerna(ternaId, { [campo]: null });
        await refetch();
      } catch (err) {
        setActionError(err instanceof Error ? err.message : 'Error al desvincular requisito');
      }
    },
    [refetch]
  );

  // Agrupar ternas seleccionadas
  const handleAgruparTernas = useCallback(
    async (ternaIds: number[]) => {
      if (!selectedCombinacionId) return;
      setActionError(null);
      try {
        await sysadminService.agruparTernas(selectedCombinacionId, { ternaIds });
        await refetch();
      } catch (err) {
        setActionError(err instanceof Error ? err.message : 'Error al agrupar ternas');
      }
    },
    [refetch, selectedCombinacionId]
  );

  // Desagrupar una terna de su grupo
  const handleDesagruparTerna = useCallback(
    async (ternaId: number) => {
      if (!selectedCombinacionId) return;
      setActionError(null);
      try {
        await sysadminService.desagruparTerna(selectedCombinacionId, ternaId);
        await refetch();
      } catch (err) {
        setActionError(err instanceof Error ? err.message : 'Error al desagrupar terna');
      }
    },
    [refetch, selectedCombinacionId]
  );

  // Crear nueva terna
  const handleCrearTerna = useCallback(
    async (requisitoId: string, columnaOrden: number) => {
      if (!selectedCombinacionId) return;
      setActionError(null);
      try {
        const campo = `requisitoC${columnaOrden}Id` as 'requisitoC1Id' | 'requisitoC2Id' | 'requisitoC3Id';
        await sysadminService.crearTerna(selectedCombinacionId, { [campo]: requisitoId });
        await refetch();
      } catch (err) {
        setActionError(err instanceof Error ? err.message : 'Error al crear terna');
      }
    },
    [refetch, selectedCombinacionId]
  );

  // Insertar terna entre filas
  const handleInsertarTerna = useCallback(
    async (requisitoId: string, columnaOrden: number, insertAtOrden: number) => {
      if (!selectedCombinacionId) return;
      setActionError(null);
      try {
        await sysadminService.insertarTerna(selectedCombinacionId, {
          requisitoId,
          columnaOrden,
          insertAtOrden,
        });
        await refetch();
      } catch (err) {
        setActionError(err instanceof Error ? err.message : 'Error al insertar terna');
      }
    },
    [refetch, selectedCombinacionId]
  );

  // Editar requisito desde la matriz
  const handleEditarRequisito = useCallback(
    (req: RequisitoAsociacion) => {
      const ntcNombre = data?.combinacion.ntcs.find((n) => n.ntcId === req.ntcId)?.nombre ?? '';
      setEditingRequisito({
        requisitoId: req.requisitoId,
        ntcId: req.ntcId,
        ntcNombre,
        capitulo: req.capitulo,
        numeral: req.numeral,
        descripcion: req.descripcion,
        posibleEvidencia: null,
      });
      setShowEditModal(true);
    },
    [data]
  );
  const handleCheckViabilidad = useCallback(async (requisitoId: string) => {
    setCheckingViabilidad(true);
    setActionError(null);
    try {
      const res = await sysadminService.viabilidadEliminacionRequisito(requisitoId);
      setViabilidadResult(res);
      setShowViabilidadModal(true);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Error al verificar viabilidad');
    } finally {
      setCheckingViabilidad(false);
    }
  }, []);

  const handleVerAnalisis = useCallback(async (requisitoId: string) => {
    setLoadingAnalysis(true);
    setActionError(null);
    try {
      const res = await sysadminService.getRequisitoAnalisis(requisitoId);
      if (res.success) {
        setAnalysisData(res.analisis);
        setShowAnalysisModal(true);
      } else {
        setActionError('No se pudo obtener el análisis del requisito');
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Error al obtener análisis');
    } finally {
      setLoadingAnalysis(false);
    }
  }, []);

  const handleIniciarMigracion = useCallback((requisitoId: string) => {
    setTargetRequisitoId(requisitoId); // El primero seleccionado es el que queremos CONSERVAR (Destino)
    setMigrationStep(1);
    setSourceRequisitoId(null);
    setMigrationResult(null);
    setIsSelectingTarget(true); // Entrar en modo selección interactiva para el Origen
    setShowAnalysisModal(false);
  }, []);

  const handleSelectTarget = useCallback(
    (sourceId: string) => {
      if (!targetRequisitoId || sourceId === targetRequisitoId) {
        if (sourceId === targetRequisitoId) {
          setActionError('No puedes fusionar un requisito consigo mismo');
        }
        return;
      }
      setSourceRequisitoId(sourceId); // El segundo seleccionado es el que queremos ELIMINAR (Origen)
      setIsSelectingTarget(false);

      // Validar que sean de la misma NTC
      const targetReq = requisitosMap.get(targetRequisitoId);
      const sourceReq = requisitosMap.get(sourceId);

      if (sourceReq && targetReq && sourceReq.ntcId !== targetReq.ntcId) {
        setActionError('Solo puedes fusionar requisitos que pertenecen a la misma NTC');
        setSourceRequisitoId(null);
        setTargetRequisitoId(null);
        return;
      }

      setMigrationStep(1); // Paso 1: Revisión de Impacto / Confirmación
      setShowMigrationWizard(true);
    },
    [targetRequisitoId, requisitosMap]
  );

  // Cargar análisis para el wizard cuando cambian los IDs
  useEffect(() => {
    async function loadAnalysis() {
      if (!showMigrationWizard) return;
      setLoadingWizardAnalysis(true);
      try {
        const promises = [];
        if (sourceRequisitoId) promises.push(sysadminService.getRequisitoAnalisis(sourceRequisitoId));
        if (targetRequisitoId) promises.push(sysadminService.getRequisitoAnalisis(targetRequisitoId));

        const results = await Promise.all(promises);
        if (sourceRequisitoId) {
          const resS = results[0];
          if (resS.success) setSourceAnalysis(resS.analisis);
        }
        if (targetRequisitoId) {
          const resT = sourceRequisitoId ? results[1] : results[0];
          if (resT?.success) setTargetAnalysis(resT.analisis);
        }
      } catch (err) {
        console.error('Error cargando análisis en wizard:', err);
      } finally {
        setLoadingWizardAnalysis(false);
      }
    }
    loadAnalysis();
  }, [showMigrationWizard, sourceRequisitoId, targetRequisitoId]);

  const handleEjecutarMigracion = useCallback(async () => {
    if (!sourceRequisitoId || !targetRequisitoId) return;
    setMigrating(true);
    setActionError(null);
    try {
      const res = await sysadminService.migrarDatosRequisito({
        sourceRequisitoId,
        targetRequisitoId,
      });
      if (res.success) {
        setMigrationResult(res.data);
        setMigrationStep(2); // Paso 2: Éxito
        await refetch();
      } else {
        setActionError('Error al migrar los datos');
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Error al migrar');
    } finally {
      setMigrating(false);
    }
  }, [sourceRequisitoId, targetRequisitoId, refetch]);

  const handleSwapSourceTarget = useCallback(() => {
    if (!sourceRequisitoId || !targetRequisitoId) return;
    const oldSource = sourceRequisitoId;
    setSourceRequisitoId(targetRequisitoId);
    setTargetRequisitoId(oldSource);
  }, [sourceRequisitoId, targetRequisitoId]);

  const handleEditSuccess = useCallback(() => {
    setShowEditModal(false);
    setEditingRequisito(null);
    refetch();
  }, [refetch]);

  // Reordenar: llamar API y refetch
  const handleReordenar = useCallback(
    async (reorden: { id: number; orden: number }[]) => {
      if (!selectedCombinacionId) return;
      setActionError(null);
      try {
        await sysadminService.reordenarTernas(selectedCombinacionId, { ordenes: reorden });
        await refetch();
      } catch (err) {
        setActionError(err instanceof Error ? err.message : 'Error al reordenar ternas');
      }
    },
    [refetch, selectedCombinacionId]
  );

  // Eliminar requisito huérfano (sin terna)
  const handleEliminarRequisito = useCallback(
    async (requisitoId: string) => {
      if (!window.confirm('¿Eliminar este requisito definitivamente? Esta acción no se puede deshacer.')) return;
      setActionError(null);
      try {
        await sysadminService.eliminarRequisito(requisitoId);
        await refetch();
      } catch (err) {
        setActionError(err instanceof Error ? err.message : 'Error al eliminar el requisito');
      }
    },
    [refetch]
  );
  const handleEliminarTerna = useCallback(
    async (ternaIds: number[]) => {
      if (!window.confirm('¿Estás seguro de que quieres eliminar este bloque completo de ternas?')) return;
      setActionError(null);
      try {
        await Promise.all(ternaIds.map((id) => sysadminService.eliminarTerna(id)));
        await refetch();
      } catch (err) {
        setActionError(err instanceof Error ? err.message : 'Error al eliminar las ternas');
      }
    },
    [refetch]
  );

  if (loadingCombinaciones) return <LoadingContainer text="Cargando combinaciones..." />;

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Asociaciones entre Requisitos</h2>
        <p className="text-sm text-gray-500">
          Configura las relaciones entre requisitos de distintas NTCs. Selecciona una combinación para ver y editar.
        </p>
      </div>

      {/* Selector de combinación + toggle edición */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
        <div className="w-full sm:w-80">
          <label className="block text-sm font-medium text-gray-700 mb-1">Combinación de NTCs</label>
          <Select
            value={selectedCombinacionId?.toString() || ''}
            onChange={(e) => {
              const val = e.target.value ? parseInt(e.target.value) : undefined;
              setSelectedCombinacionId(val);
              setEditMode(false);
              setActionError(null);
            }}
            options={[
              { value: '', label: '— Seleccionar combinación —' },
              ...combinaciones.map((c) => ({
                value: c.combinacionId.toString(),
                label: c.nombre,
              })),
            ]}
          />
        </div>
        {data && (
          <>
            <button
              type="button"
              onClick={() => setEditMode((prev) => !prev)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors border ${
                editMode ? 'bg-blue-50 text-blue-700 border-blue-300' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {editMode ? <Eye className="w-4 h-4" /> : <Pencil className="w-4 h-4" />}
              {editMode ? 'Solo vista previa' : 'Editar asociaciones'}
            </button>
            <button
              type="button"
              onClick={async () => {
                if (!selectedCombinacionId) return;
                if (
                  !window.confirm(
                    "¿Estás seguro? Las asociaciones originales no se eliminarán automáticamente. Podrás hacerlo manualmente después con el botón 'Eliminar Asociaciones Legacy'."
                  )
                ) {
                  return;
                }
                setActionError(null);
                try {
                  setMigrando(true);
                  const resp = await sysadminService.migrarAsociacionesATernas(selectedCombinacionId);
                  setMigrando(false);
                  if (resp.success) {
                    await refetch();
                    alert(
                      `Migración completada:\n` +
                        `Ternas creadas: ${resp.ternasCreadas}\n` +
                        `Grupos encontrados: ${resp.gruposEncontrados}\n` +
                        `Requisitos asociados: ${resp.requisitosAsociados}\n` +
                        `Asociaciones eliminadas: ${resp.asociacionesEliminadas}`
                    );
                  } else {
                    setActionError(resp.message || 'Error en migración');
                  }
                } catch (err) {
                  setMigrando(false);
                  setActionError(err instanceof Error ? err.message : 'Error en migración');
                }
              }}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors border bg-yellow-50 text-yellow-700 border-yellow-300 hover:bg-yellow-100"
              disabled={migrando}
            >
              <Link2 className="w-4 h-4" />
              {migrando ? 'Migrando...' : 'Migrar a ternas'}
            </button>
            <button
              type="button"
              onClick={() => setShowEmpresasModal(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors border bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
            >
              <Building2 className="w-4 h-4" />
              Ver empresas
            </button>
            {data.combinacion.hasLegacyData && (
              <button
                type="button"
                onClick={async () => {
                  if (!selectedCombinacionId) return;
                  if (!window.confirm('Esto eliminará permanentemente las asociaciones legacy. Esta acción no se puede deshacer.')) {
                    return;
                  }
                  setActionError(null);
                  try {
                    await sysadminService.eliminarAsociacionesLegacyCombinacion(selectedCombinacionId);
                    await refetch();
                    alert('Asociaciones legacy eliminadas correctamente.');
                  } catch (err) {
                    setActionError(err instanceof Error ? err.message : 'Error al eliminar asociaciones legacy');
                  }
                }}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors border bg-red-50 text-red-700 border-red-300 hover:bg-red-100"
              >
                <AlertTriangle className="w-4 h-4" />
                Eliminar Asociaciones Legacy
              </button>
            )}
          </>
        )}
      </div>

      {/* Modal de Empresas */}
      <EmpresasCombinacionModal
        isOpen={showEmpresasModal}
        onClose={() => setShowEmpresasModal(false)}
        combinacionId={selectedCombinacionId}
        combinacionNombre={data?.combinacion.nombre}
      />

      {/* Errores */}
      {(error || actionError) && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-500" />
          <p className="text-sm text-red-700">{error || actionError}</p>
        </div>
      )}

      {/* Loading */}
      {isLoading && <LoadingContainer text="Cargando asociaciones..." />}

      {/* Sin selección */}
      {!selectedCombinacionId && !isLoading && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <Link2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Selecciona una combinación de NTCs para ver las asociaciones entre sus requisitos</p>
        </div>
      )}

      {/* Contenido con datos */}
      {data && !isLoading && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-gray-900">{stats.totalRequisitos}</p>
              <p className="text-xs text-gray-500">Requisitos totales</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-blue-600">{stats.totalTernas}</p>
              <p className="text-xs text-gray-500">Ternas</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-green-600">{stats.clusters}</p>
              <p className="text-xs text-gray-500">Clusters</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-amber-600">{stats.reqsSinAsociar}</p>
              <p className="text-xs text-gray-500">Sin asociar</p>
            </div>
          </div>

          {/* NTCs badges */}
          <div className="flex gap-2 flex-wrap">
            {data.combinacion.ntcs
              .sort((a, b) => a.orden - b.orden)
              .map((ntc) => (
                <Badge key={ntc.ntcId} variant="blue">
                  {ntc.orden}. {ntc.nombre} ({data.requisitos.filter((r) => r.ntcId === ntc.ntcId).length} req.)
                </Badge>
              ))}
          </div>

          {/* Matriz */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              {editMode ? (
                <>
                  <Pencil className="w-4 h-4 text-blue-600" />
                  <h3 className="text-sm font-medium text-blue-700">
                    Modo edición — usa la columna "Acciones" para asociar o desasociar requisitos
                  </h3>
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4 text-gray-500" />
                  <h3 className="text-sm font-medium text-gray-700">Vista previa de asociaciones</h3>
                </>
              )}
            </div>

            <div className="flex-1 bg-white p-4 lg:p-6 overflow-hidden flex flex-col min-h-0 relative">
              {/* Banner de modo selección */}
              {isSelectingTarget && (
                <div className="absolute top-4 left-4 right-4 z-60 bg-blue-600 text-white p-5 rounded-2xl shadow-2xl flex items-center justify-between border-2 border-blue-400 animate-in slide-in-from-top-6 duration-300">
                  <div className="flex items-center gap-4">
                    <div className="bg-white/20 p-3 rounded-xl backdrop-blur-sm">
                      <Link2 className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="font-bold text-lg">Modo Selección de Origen (A eliminar)</p>
                      <div className="flex items-center gap-2 text-sm text-blue-100 mt-0.5">
                        <span>Destino (Conservar):</span>
                        <Badge variant="warning" className="bg-white/10 border-white/20 text-white py-0.5 px-2">
                          {requisitosMap.get(targetRequisitoId || '')?.numeral || '...'}
                        </Badge>
                        <span className="opacity-70">—</span>
                        <span className="max-w-md truncate italic text-xs">{requisitosMap.get(targetRequisitoId || '')?.descripcion}</span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsSelectingTarget(false);
                      setTargetRequisitoId(null);
                    }}
                    className="px-5 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl text-sm font-bold transition-all border border-white/30 hover:shadow-lg active:scale-95"
                  >
                    Cancelar selección
                  </button>
                </div>
              )}

              <MatrizAsociaciones
                grupos={grupos}
                ntcs={data.combinacion.ntcs}
                todosRequisitos={data.requisitos}
                editable={editMode}
                onAsignarRequisito={handleAsignarRequisito}
                onDesvincularRequisito={handleDesvincularRequisito}
                onReordenar={handleReordenar}
                onEliminarTerna={handleEliminarTerna}
                onAgruparTernas={handleAgruparTernas}
                onDesagruparTerna={handleDesagruparTerna}
                onCrearTerna={handleCrearTerna}
                onInsertarTerna={handleInsertarTerna}
                onEditarRequisito={handleEditarRequisito}
                selectionMode={isSelectingTarget}
                onSelectTarget={handleSelectTarget}
                sourceNtcId={targetRequisitoId ? requisitosMap.get(targetRequisitoId)?.ntcId : undefined}
                onShowAnalysis={handleVerAnalisis}
                onIniciarMigracion={handleIniciarMigracion}
              />
            </div>
          </div>

          {/* Requisitos sin terna */}
          {data.requisitosSinTerna.length > 0 &&
            (() => {
              const reqMap = new Map(data.requisitos.map((r) => [r.requisitoId, r]));
              const ntcMap = new Map(data.combinacion.ntcs.map((n) => [n.ntcId, n.nombre]));
              const sinTerna = data.requisitosSinTerna.map((id) => reqMap.get(id)).filter(Boolean) as typeof data.requisitos;
              return (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    <h3 className="text-sm font-medium text-amber-700">Requisitos sin terna ({sinTerna.length})</h3>
                    <span className="text-xs text-gray-500">
                      — Estos requisitos no están asociados a ninguna terna y no aparecerán en la evaluación.
                    </span>
                  </div>
                  <div className="border border-amber-200 rounded-lg overflow-visible bg-white">
                    <table className="w-full text-sm ">
                      <thead className="bg-amber-50 border-b border-amber-200">
                        <tr>
                          <th className="px-4 py-2 text-left font-semibold text-amber-800">Capítulo</th>
                          <th className="px-4 py-2 text-left font-semibold text-amber-800">Numeral</th>
                          <th className="px-4 py-2 text-left font-semibold text-amber-800">Descripción</th>
                          <th className="px-4 py-2 text-left font-semibold text-amber-800">NTC</th>
                          {editMode && <th className="px-4 py-2 text-center font-semibold text-amber-800">Acción</th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-amber-100">
                        {sinTerna.map((req) => (
                          <tr
                            key={req.requisitoId}
                            onClick={() => isSelectingTarget && handleSelectTarget(req.requisitoId)}
                            className={`bg-white hover:bg-amber-50/40 transition-colors ${
                              isSelectingTarget ? 'cursor-pointer hover:bg-blue-50 group hover:ring-2 hover:ring-blue-400' : ''
                            }`}
                          >
                            <td className="px-4 py-2 text-gray-800 align-top whitespace-normal">{req.capitulo}</td>
                            <td className="px-4 py-2 text-gray-800 align-top whitespace-normal" title={`UUID: ${req.requisitoId}`}>
                              {req.numeral}
                            </td>
                            <td className="px-4 py-2 text-gray-600 align-top max-w-md">
                              <span className="">{req.descripcion}</span>
                            </td>
                            <td className="px-4 py-2 text-gray-600 align-top whitespace-normal">
                              {ntcMap.get(req.ntcId) ?? `NTC ${req.ntcId}`}
                            </td>
                            {editMode && (
                              <td className="px-4 py-2 text-center align-top relative">
                                <button
                                  type="button"
                                  onClick={() => setOpenMenuId(openMenuId === req.requisitoId ? null : req.requisitoId)}
                                  className="flex items-center gap-1 mx-auto px-3 py-1.5 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all shadow-sm"
                                >
                                  Acciones
                                  <ChevronDown
                                    className={`w-3.5 h-3.5 transition-transform ${openMenuId === req.requisitoId ? 'rotate-180' : ''}`}
                                  />
                                </button>

                                {openMenuId === req.requisitoId && (
                                  <>
                                    <div className="fixed inset-0 z-100" onClick={() => setOpenMenuId(null)} />
                                    <div className="absolute right-4 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-2xl z-110 overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150">
                                      <button
                                        onClick={() => {
                                          handleVerAnalisis(req.requisitoId);
                                          setOpenMenuId(null);
                                        }}
                                        className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-left text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                                      >
                                        <Info className="w-3.5 h-3.5" />
                                        Análisis detallado
                                      </button>
                                      <button
                                        onClick={() => {
                                          handleIniciarMigracion(req.requisitoId);
                                          setOpenMenuId(null);
                                        }}
                                        className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-left text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                                      >
                                        <Link2 className="w-3.5 h-3.5" />
                                        Fusionar / Migrar
                                      </button>
                                      <div className="border-t border-gray-100" />
                                      <button
                                        onClick={() => {
                                          handleEliminarRequisito(req.requisitoId);
                                          setOpenMenuId(null);
                                        }}
                                        className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-left text-red-600 hover:bg-red-50 transition-colors"
                                      >
                                        <Trash2 className="w-3.5 h-3.5" />
                                        Eliminar permanente
                                      </button>
                                    </div>
                                  </>
                                )}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}
        </>
      )}
      {/* Modal de edición de requisito */}
      <EditarRequisitoModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingRequisito(null);
        }}
        onSuccess={handleEditSuccess}
        requisito={editingRequisito}
        onSave={sysadminService.actualizarRequisito}
      />

      {/* Modal de Análisis Detallado */}
      {showAnalysisModal && analysisData && (
        <Modal
          isOpen={showAnalysisModal}
          onClose={() => setShowAnalysisModal(false)}
          title={`Análisis de Requisito: ${analysisData.requisito.codigo}`}
          size="lg"
        >
          <div className="space-y-6">
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
              <h4 className="text-sm font-semibold text-gray-900 mb-1">{analysisData.requisito.norma}</h4>
              <p className="text-sm text-gray-700">{analysisData.requisito.descripcion}</p>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-100 text-center">
                <span className="block text-2xl font-bold text-blue-700">{analysisData.totales.ternas}</span>
                <span className="text-xs text-blue-600 uppercase font-semibold">Ternas</span>
              </div>
              <div className="bg-green-50 p-3 rounded-lg border border-green-100 text-center">
                <span className="block text-2xl font-bold text-green-700">{analysisData.totales.empresas}</span>
                <span className="text-xs text-green-600 uppercase font-semibold">Empresas</span>
              </div>
              <div className="bg-purple-50 p-3 rounded-lg border border-purple-100 text-center">
                <span className="block text-2xl font-bold text-purple-700">{analysisData.totales.evaluaciones}</span>
                <span className="text-xs text-purple-600 uppercase font-semibold">Evaluaciones</span>
              </div>
            </div>

            {/* Ternas */}
            <div>
              <h5 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-2">
                <Link2 className="w-4 h-4 text-gray-400" />
                Uso en Ternas / Combinaciones
              </h5>
              {analysisData.ternas.length > 0 ? (
                <div className="max-h-32 overflow-y-auto border border-gray-100 rounded-lg">
                  <table className="w-full text-xs text-left">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2">Combinación</th>
                        <th className="px-3 py-2">Posición</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {analysisData.ternas.map((t) => (
                        <tr key={t.id}>
                          <td className="px-3 py-2 text-gray-800">{t.combinacionNombre}</td>
                          <td className="px-3 py-2">
                            <Badge variant="blue">{t.posicion}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-xs text-gray-500 italic">No se usa en ninguna terna actualmente.</p>
              )}
            </div>

            {/* Empresas */}
            <div>
              <h5 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-gray-400" />
                Empresas Asociadas
              </h5>
              {analysisData.empresas.length > 0 ? (
                <div className="max-h-32 overflow-y-auto border border-gray-100 rounded-lg">
                  <table className="w-full text-xs text-left">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2">Empresa</th>
                        <th className="px-3 py-2">Estado</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {analysisData.empresas.map((e) => (
                        <tr key={e.nit}>
                          <td className="px-3 py-2 text-gray-800">{e.razonSocial}</td>
                          <td className="px-3 py-2">
                            <Badge variant={e.calificacion === 'C' ? 'green' : e.calificacion === 'NC' ? 'red' : 'warning'}>
                              {e.calificacion}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-xs text-gray-500 italic">No hay empresas asociadas.</p>
              )}
            </div>

            <button
              onClick={() => {
                setShowAnalysisModal(false);
                handleIniciarMigracion(analysisData.requisito.id);
              }}
              className="w-full py-2.5 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-sm"
            >
              Iniciar Migración / Fusión de Datos
            </button>
          </div>
        </Modal>
      )}

      {/* Modal - Wizard de Migración */}
      {showMigrationWizard && sourceRequisitoId && (
        <Modal
          isOpen={showMigrationWizard}
          onClose={() => !migrating && setShowMigrationWizard(false)}
          title="Migración y Fusión de Requisitos"
          size="lg"
        >
          <div className="space-y-6">
            {/* Steps indicator */}
            <div className="flex items-center justify-center gap-10 mb-8">
              {[1, 2].map((s) => (
                <div key={s} className="flex items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                      migrationStep >= s ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-400'
                    }`}
                  >
                    {s}
                  </div>
                  {s < 2 && <div className={`w-16 h-1 ${migrationStep > s ? 'bg-blue-600' : 'bg-gray-200'}`} />}
                </div>
              ))}
            </div>

            {migrationStep === 1 && (
              <div className="space-y-4 text-sm">
                <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                  <h4 className="text-sm font-bold text-amber-800 mb-1">Paso 1: Confirmación de Impacto</h4>
                  <p className="text-xs text-amber-700">
                    Se consolidarán permanentemente todas las ternas, empresas y evaluaciones del origen al destino.
                  </p>
                </div>

                <div className="flex items-center justify-center gap-4">
                  {/* Origen Card */}
                  <div className="flex-1 p-4 border border-red-200 rounded-xl bg-red-50/30 text-red-700 flex flex-col items-center text-center">
                    <span className="text-[10px] uppercase font-bold px-2 py-0.5 bg-red-100 rounded-full mb-3 inline-block">
                      Eliminar (Origen)
                    </span>
                    <div className="w-full space-y-2">
                      <p className="text-lg font-black tracking-tight leading-none">
                        {requisitosMap.get(sourceRequisitoId || '')?.numeral || '...'}
                      </p>
                      <p className="text-[11px] text-gray-600 line-clamp-3 leading-tight min-h-10 px-2 italic">
                        &quot;{requisitosMap.get(sourceRequisitoId || '')?.descripcion || 'No se cargó la descripción'}&quot;
                      </p>

                      {/* Stats del origen */}
                      <div className="pt-2 border-t border-red-100 flex flex-wrap justify-center gap-x-4 gap-y-1 text-[10px] text-red-600/80 font-bold uppercase tracking-wider">
                        <div className="flex gap-1 items-center bg-white px-2 py-0.5 rounded shadow-sm border border-red-50">
                          Ternas: <span>{loadingWizardAnalysis ? '...' : sourceAnalysis?.totales.ternas || 0}</span>
                        </div>
                        <div className="flex gap-1 items-center bg-white px-2 py-0.5 rounded shadow-sm border border-red-50">
                          Empresas: <span>{loadingWizardAnalysis ? '...' : sourceAnalysis?.totales.empresas || 0}</span>
                        </div>
                        <div className="flex gap-1 items-center bg-white px-2 py-0.5 rounded shadow-sm border border-red-50">
                          Eval: <span>{loadingWizardAnalysis ? '...' : sourceAnalysis?.totales.evaluaciones || 0}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col items-center shrink-0 w-16 px-1">
                    <button
                      onClick={handleSwapSourceTarget}
                      disabled={migrating}
                      className="group flex flex-col items-center transition-all hover:scale-110 active:scale-90"
                    >
                      <div className="p-3 bg-white border-2 border-blue-200 rounded-full shadow-md text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                        <ChevronsUpDown className="w-6 h-6 rotate-90" />
                      </div>
                      <span className="text-[9px] font-black text-blue-500 uppercase mt-2 tracking-tighter">Intercambiar</span>
                    </button>
                    <div className="h-4 w-px bg-linear-to-b from-blue-200 to-transparent my-1" />
                  </div>

                  {/* Destino Card */}
                  <div className="flex-1 p-4 border border-green-200 rounded-xl bg-green-50/30 text-green-800 flex flex-col items-center text-center">
                    <span className="text-[10px] uppercase font-bold px-2 py-0.5 bg-green-100 rounded-full mb-3 inline-block">
                      Conservar (Destino)
                    </span>
                    <div className="w-full space-y-2">
                      <p className="text-lg font-black tracking-tight leading-none text-green-900">
                        {requisitosMap.get(targetRequisitoId || '')?.numeral || '...'}
                      </p>
                      <p className="text-[11px] text-gray-600 line-clamp-3 leading-tight min-h-10 px-2 italic">
                        &quot;{requisitosMap.get(targetRequisitoId || '')?.descripcion || 'No se cargó la descripción'}&quot;
                      </p>

                      {/* Stats del destino */}
                      <div className="pt-2 border-t border-green-100 flex flex-wrap justify-center gap-x-4 gap-y-1 text-[10px] text-green-700/80 font-bold uppercase tracking-wider">
                        <div className="flex gap-1 items-center bg-white px-2 py-0.5 rounded shadow-sm border border-green-50">
                          Ternas: <span>{loadingWizardAnalysis ? '...' : targetAnalysis?.totales.ternas || 0}</span>
                        </div>
                        <div className="flex gap-1 items-center bg-white px-2 py-0.5 rounded shadow-sm border border-green-50">
                          Empresas: <span>{loadingWizardAnalysis ? '...' : targetAnalysis?.totales.empresas || 0}</span>
                        </div>
                        <div className="flex gap-1 items-center bg-white px-2 py-0.5 rounded shadow-sm border border-green-50">
                          Eval: <span>{loadingWizardAnalysis ? '...' : targetAnalysis?.totales.evaluaciones || 0}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-red-50 p-3 rounded-lg border border-red-100 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                  <p className="text-[11px] text-red-700">
                    Esta acción es irreversible. El requisito de origen será eliminado tras la migración exitosa de sus datos.
                  </p>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={() => setShowMigrationWizard(false)}
                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleEjecutarMigracion}
                    disabled={migrating}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    {migrating ? 'Migrando...' : 'Confirmar y Fusionar'}
                  </button>
                </div>
              </div>
            )}

            {migrationStep === 2 && migrationResult && (
              <div className="text-center space-y-4 py-4">
                <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">¡Migración Completada!</h3>
                <p className="text-sm text-gray-500">Se han migrado todos los datos exitosamente al requisito destino.</p>

                <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-2 gap-4 text-left">
                  <div>
                    <span className="text-[10px] uppercase font-bold text-gray-400 block mb-1">Ternas Actualizadas</span>
                    <p className="text-lg font-bold text-gray-900">{migrationResult.ternas}</p>
                  </div>
                  <div>
                    <span className="text-[10px] uppercase font-bold text-gray-400 block mb-1">Evaluaciones Migradas</span>
                    <p className="text-lg font-bold text-gray-900">{migrationResult.evaluaciones}</p>
                  </div>
                  <div>
                    <span className="text-[10px] uppercase font-bold text-gray-400 block mb-1">Estados Movidos</span>
                    <p className="text-lg font-bold text-gray-900">{migrationResult.estados}</p>
                  </div>
                  <div>
                    <span className="text-[10px] uppercase font-bold text-gray-400 block mb-1">Empresas Afectadas</span>
                    <p className="text-lg font-bold text-gray-900">{migrationResult.empresasAfectadas.length}</p>
                  </div>
                </div>

                <button
                  onClick={() => setShowMigrationWizard(false)}
                  className="w-full py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors mt-4"
                >
                  Cerrar
                </button>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Modal de viabilidad */}
      {showViabilidadModal && viabilidadResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden border border-gray-100 animate-in fade-in zoom-in duration-200">
            <div
              className={`px-6 py-4 flex items-center gap-3 ${viabilidadResult.viable ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-800'}`}
            >
              {viabilidadResult.viable ? <Building2 className="w-6 h-6" /> : <AlertTriangle className="w-6 h-6" />}
              <h3 className="text-lg font-bold">{viabilidadResult.viable ? 'Requisito eliminable' : 'No se puede eliminar'}</h3>
            </div>

            <div className="p-6 space-y-4">
              {viabilidadResult.viable ? (
                <p className="text-gray-600">
                  Este requisito no está siendo utilizado en ninguna terna de esta u otras combinaciones. Puede ser eliminado de forma
                  segura.
                </p>
              ) : (
                <>
                  <p className="text-gray-700 font-medium">El requisito está asociado a las siguientes combinaciones:</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-600 text-sm bg-gray-50 p-3 rounded-lg border border-gray-100">
                    {viabilidadResult.causas.map((causa, i) => (
                      <li key={i}>{causa}</li>
                    ))}
                  </ul>
                  <p className="text-xs text-amber-700 bg-amber-50 p-2 rounded border border-amber-100">
                    Debe desvincular el requisito de todas las ternas en estas combinaciones antes de poder eliminarlo globalmente.
                  </p>
                </>
              )}

              <button
                type="button"
                onClick={() => setShowViabilidadModal(false)}
                className="w-full py-2.5 px-4 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-colors shadow-sm"
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
